import React, { useState, useEffect } from 'react'
import { Link, Redirect, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'

import { toast } from 'react-toastify'
import * as yup from 'yup'
import { Helmet } from 'react-helmet'
import cloneDeep from 'lodash/cloneDeep'

import { useConfig } from '@peracto/peracto-config'

import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { CREATE, GET_ONE, useClient, getSchemaFromResource } from '@peracto/client'

import { MODE_ADD } from '../ProductVariantsForm'

import { formatDataForAPI, convertPricesToIntegers, getProductName } from '../../Products/util'

const ProductAdd = ({ ProductVariantsForm }) => {
    const { client, getResource } = useClient()
    const [redirect, setRedirect] = useState()
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [product, setProduct] = useState()
    const [parentProduct, setParentProduct] = useState()
    const [productResources, setProductResources] = useState({})

    const [loading, setLoading] = useState(true)
    const { id } = useParams()

    const config = useConfig()
    const { products } = config.get('features', {})

    const additionalPriceTypes = products?.additionalPriceTypes ?? []
    const showPricesAsCurrency = products?.showPricesAsCurrency ?? true

    const schema = getSchemaFromResource(getResource('product-variants')).shape({
        mpn: yup.string().nullable(),
        gtin: yup.string().nullable(),
    })

    const formatDataForVariantForm = productOptions => {
        const options = []

        !!productOptions &&
            productOptions.forEach(option => {
                options.push({
                    displayValue: '',
                    identifier: '',
                    altValue: null,
                    productOption: option,
                })
            })

        return {
            sku: '',
            options,
        }
    }

    const fetchProductOptions = async () => {
        setLoading(true)
        try {
            const { data: productData, response } = await client(GET_ONE, 'products', {
                id: `/products/${id}`,
            })

            const resources = {}
            productData?.resources?.forEach(
                r =>
                    (resources[r.type] =
                        resources[r.type]?.length > 0 ? [...resources[r.type], r] : [r])
            )

            const formatted = formatDataForVariantForm(productData.options)
            setProduct(formatted)
            setProductResources(resources)

            setParentProduct({ sku: productData.sku, name: getProductName(productData) })
            setLoading(false)

            if (response.status === 404) {
                setRedirect(`/products/${id}/variants`)
            }
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            setLoading(false)
            setRedirect(`/products/${id}/variants`)
        }
    }

    const onSubmit = async (data, actions) => {
        const cloneData = cloneDeep(data)

        let formattedData

        if (showPricesAsCurrency) {
            formattedData = convertPricesToIntegers(data, additionalPriceTypes)
            formattedData = formatDataForAPI({ data: formattedData, isVariant: true })
        } else {
            formattedData = formatDataForAPI({ data, isVariant: true })
        }

        try {
            const response = await client(CREATE, 'product-variants', {
                data: {
                    ...formattedData,
                    product: `/products/${id}`,
                },
            })

            if (response?.data?.originId) {
                setRedirect(`/products/${id}/variants/${response.data.originId}`)
            } else {
                setRedirect(`/products/${id}/variants`)
            }

            toast.success('Variant successfully added!')
            actions.setSubmitting(false)
        } catch (e) {
            if (e?.error?.body?.violations?.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                e.error.body.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            }
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
            actions.setValues(cloneData)
            actions.setSubmitting(false)
        }
    }

    useEffect(() => {
        fetchProductOptions()
    }, [])

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url={`/products`}
                message="The product you're looking for could not be found"
                buttonLabel="Go to Products"
            />
        )
    }

    if (redirect) {
        return <Redirect to={redirect} />
    }

    return (
        <>
            <Helmet>
                <title>Product Variants | Add | Peracto</title>
            </Helmet>

            {loading ? (
                <div className="form-container">
                    <div className="card">
                        <div className="card-body">Loading...</div>
                    </div>
                </div>
            ) : (
                <div className="form-container">
                    <Heading
                        name={`Add Product Variant of ${parentProduct.name} (${parentProduct.sku})`}
                    >
                        <div className="!grow !flex !items-center !justify-end">
                            <Link
                                className="btn btn-outline-primary !whitespace-nowrap"
                                to={`/products/${id}/variants`}
                                data-testid="back-to-product-variants"
                            >
                                <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                                Back to Product Variants
                            </Link>
                        </div>
                    </Heading>
                    <ProductVariantsForm
                        onSubmit={onSubmit}
                        mode={MODE_ADD}
                        schema={schema}
                        values={product}
                        productResources={productResources}
                    />
                </div>
            )}
        </>
    )
}

export default ProductAdd
